import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import axios from 'axios';
import useSWR from 'swr';
import ChartComponent from './Chart';
import MonitorStock from './MonitorStock';
import { MonitorGudang } from './MonitorGudang';
import { useSelector } from "react-redux";
import { DashboardJurnal } from './DashboardJurnal';
import { Users, Package, ShoppingCart, UsersThree, Storefront, ArrowClockwise } from '@phosphor-icons/react';
import { waktuCabang } from '../utils/waktuCabang';
import { apiHost } from '../utils/apiHost';
import { StatistikDashboard, JudulBagian, KartuGrafik } from './dashboard/StatistikDashboard';
import RingkasanLanggananSuperadmin from './langganan/RingkasanLanggananSuperadmin';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = (url) => axios.get(url, { withCredentials: true }).then((res) => res.data);

const LABEL_STATUS = { settlement: 'Berhasil', success: 'Berhasil', pending: 'Menunggu', cancel: 'Batal', expire: 'Kedaluwarsa' };
const WARNA_STATUS = { settlement: '#4caf50', success: '#4caf50', pending: '#ff9800', cancel: '#ef5350', expire: '#ef5350' };

const bulanSekarang = () => {
  const d = waktuCabang(new Date());
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  // Kartu yang memanggil endpoint berfitur disembunyikan bila paket cabang tidak memilikinya (hindari 403 saat dashboard dimuat).
  const punyaFitur = (kunci) => !user?.langganan || user.langganan.fitur.includes(kunci);
  const [bulan, setBulan] = useState(bulanSekarang);
  const [cabang, setCabang] = useState('');

  // Semua kartu & grafik dalam satu permintaan; admin otomatis dibatasi ke cabangnya oleh server.
  const statUrl = user
    ? `${getApiBaseUrl()}/dashboard/statistik?bulan=${bulan}${superadmin && cabang ? `&cabangUuid=${cabang}` : ''}`
    : null;
  const { data: stat, error: statError, isValidating: statMemuat, mutate: muatUlangStat } = useSWR(statUrl, fetcher, { keepPreviousData: true });

  const { data: summaryRes, mutate: muatUlangSummary } = useSWR(user ? `${getApiBaseUrl()}/dashboardsummary` : null, fetcher);
  const summary = summaryRes?.status ? summaryRes.data : null;

  const { data: cabangList } = useSWR(superadmin ? `${getApiBaseUrl()}/cabang` : null, fetcher);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const galatStat = statError ? (statError.response?.data?.message || statError.message) : null;

  // Simpan target omzet bulan yang dilihat (cabang dipilih / cabang admin), lalu muat ulang statistik.
  const aturTarget = async (target) => {
    await axios.put(`${getApiBaseUrl()}/target-penjualan`, { bulan, target, ...(superadmin ? { cabanguuid: cabang } : {}) }, { withCredentials: true });
    await muatUlangStat();
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Judul + filter */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            {stat?.data?.cabang?.nama || (superadmin ? 'Semua cabang' : user?.cabang?.namacabang || '')}
            {' · '}
            {waktuCabang(new Date()).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Bulan"
            type="month"
            size="small"
            value={bulan}
            onChange={(e) => e.target.value && setBulan(e.target.value)}
            inputProps={{ max: bulanSekarang() }}
            InputLabelProps={{ shrink: true }}
            sx={{ bgcolor: 'white', width: 170 }}
          />
          {superadmin && (
            <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white' }}>
              <InputLabel shrink>Cabang</InputLabel>
              <Select
                value={cabang}
                label="Cabang"
                notched
                displayEmpty
                renderValue={(v) => (v ? cabangList?.data?.find((c) => c.uuid === v)?.namacabang || '-' : 'Semua cabang')}
                onChange={(e) => setCabang(e.target.value)}
              >
                <MenuItem value="">Semua cabang</MenuItem>
                {cabangList?.data?.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Muat ulang">
            <IconButton onClick={() => { muatUlangStat(); muatUlangSummary(); }} sx={{ bgcolor: 'white' }}>
              <ArrowClockwise size={20} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Hanya superadmin: cabang yang sudah expired / segera berakhir (H-30) */}
      {superadmin && <RingkasanLanggananSuperadmin />}

      <StatistikDashboard
        data={stat?.data}
        memuat={statMemuat}
        galat={galatStat}
        superadmin={superadmin}
        semuaCabang={!cabang}
        aturTarget={aturTarget}
      />

      {/* Data umum + transaksi terbaru */}
      <JudulBagian>Data & transaksi terbaru</JudulBagian>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            {[
              { label: 'Cabang', value: summary?.totalCabang, color: '#3f51b5', icon: <Storefront size={20} weight="bold" />, tampil: superadmin },
              { label: 'Pengguna', value: summary?.totalUsers, color: '#1e88e5', icon: <Users size={20} weight="bold" />, tampil: true },
              { label: 'Produk', value: summary?.totalProducts, color: '#26a69a', icon: <Package size={20} weight="bold" />, tampil: true },
              { label: 'Pesanan', value: summary?.totalOrders, color: '#ffa726', icon: <ShoppingCart size={20} weight="bold" />, tampil: true },
              { label: 'Pelanggan', value: summary?.totalCustomers, color: '#ab47bc', icon: <UsersThree size={20} weight="bold" />, tampil: true },
            ].filter((s) => s.tampil).map((s) => (
              <Grid item xs={6} key={s.label}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: `${s.color}1F`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{(s.value ?? 0).toLocaleString('id-ID')}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} md={8}>
          <KartuGrafik judul="Transaksi terbaru">
            <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { py: 1, px: 1, borderBottom: '1px solid #f3f3f3', fontSize: 13, textAlign: 'left' }, '& th': { color: '#888', fontWeight: 500 } }}>
                <thead>
                  <tr>{['Order ID', 'Pelanggan', 'Status', 'Total', 'Tanggal'].map((h) => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {(stat?.data?.terbaru || []).map((t) => (
                    <tr key={t.orderId}>
                      <td style={{ fontWeight: 600 }}>{t.orderId}</td>
                      <td>{t.nama}</td>
                      <td>
                        <Chip label={LABEL_STATUS[t.status] || t.status} size="small" sx={{ bgcolor: WARNA_STATUS[t.status] || '#90a4ae', color: 'white', fontWeight: 600, height: 22 }} />
                      </td>
                      <td>Rp {Number(t.total || 0).toLocaleString('id-ID')}</td>
                      <td>{formatDate(t.tanggal)}</td>
                    </tr>
                  ))}
                  {stat?.data && !stat.data.terbaru?.length && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>Belum ada transaksi</td></tr>
                  )}
                </tbody>
              </Box>
            </Box>
          </KartuGrafik>
        </Grid>
      </Grid>

      {/* Monitor stok & jurnal (komponen lama) */}
      <JudulBagian>Monitor</JudulBagian>
      <Grid container spacing={2}>
        {superadmin && <DashboardJurnal />}
        <ChartComponent />
        {punyaFitur('stok') && (
          <Grid item xs={12} md={superadmin ? 6 : 12}>
            <MonitorStock />
          </Grid>
        )}
        {superadmin && (
          <Grid item xs={12} md={6}>
            <MonitorGudang />
          </Grid>
        )}
      </Grid>

    </Box>
  );
};
